export type ElementCategory = 'Nature' | 'Life' | 'Civilization' | 'Food' | 'Concept' | 'Modern' | 'Mythology' | 'Space' | 'Art' | 'Other';

export interface ElementInfo {
  name: string;
  icon: string;
  imageUrl?: string;
  description?: string;
  tier: number;
  category: ElementCategory;
  origin: 'base' | 'known' | 'generated' | 'milestone';
  depth?: number;
  discoveredCount?: number;
}

export interface MilestoneDefinition {
  threshold: number;
  name: string;
  description: string;
  icon: string;
  imageUrl: string;
  tier: number;
  category: ElementCategory;
}

export const MILESTONES: readonly MilestoneDefinition[] = [
  { threshold: 80, name: 'Hotness', description: "You awakened 'Hotness'!", icon: '🔥', imageUrl: '/elements/hotness.webp', tier: 3, category: 'Concept' },
  { threshold: 250, name: 'Coldness', description: "You awakened 'Coldness'!", icon: '❄️', imageUrl: '/elements/coldness.webp', tier: 4, category: 'Concept' },
  { threshold: 500, name: 'Dryness', description: "You awakened 'Dryness'!", icon: '🏜️', imageUrl: '/elements/dryness.webp', tier: 5, category: 'Concept' },
  { threshold: 800, name: 'Humidity', description: "You awakened 'Humidity'!", icon: '💧', imageUrl: '/elements/humidity.webp', tier: 6, category: 'Concept' },
];

export function getMilestoneElement(milestone: MilestoneDefinition): ElementInfo {
  return {
    name: milestone.name,
    icon: milestone.icon,
    imageUrl: milestone.imageUrl,
    tier: milestone.tier,
    category: milestone.category,
    origin: 'milestone',
  };
}

export function getNextMilestone(count: number): MilestoneDefinition | null {
  return MILESTONES.find((m) => count < m.threshold) ?? null;
}

export function getEarnedMilestones(count: number): MilestoneDefinition[] {
  return MILESTONES.filter((m) => count >= m.threshold);
}

export interface KnownRecipe {
  a: string;
  b: string;
  result: Omit<ElementInfo, 'origin'>;
}

export const BASE_ELEMENTS: readonly ElementInfo[] = [
  { name: 'Fire', icon: '🔥', imageUrl: '/elements/fire.webp', tier: 0, category: 'Nature', origin: 'base', description: 'The primordial force that burns and illuminates all.' },
  { name: 'Water', icon: '💧', imageUrl: '/elements/water.webp', tier: 0, category: 'Nature', origin: 'base', description: 'A humble element flowing to the lowest places.' },
  { name: 'Earth', icon: '🌍', imageUrl: '/elements/earth.webp', tier: 0, category: 'Nature', origin: 'base', description: 'The patient ground that embraces all things.' },
  { name: 'Wind', icon: '💨', imageUrl: '/elements/wind.webp', tier: 0, category: 'Nature', origin: 'base', description: 'Invisible yet reaching everywhere, a free spirit.' }
];

const KNOWN_RECIPE_LIST: readonly KnownRecipe[] = [
  { a: 'Water', b: 'Fire', result: { name: 'Steam', icon: '☁️', tier: 1, category: 'Nature', description: 'A breath rising from the union of fire and water.' } },
  { a: 'Water', b: 'Earth', result: { name: 'Mud', icon: '🟫', tier: 1, category: 'Nature', description: 'Earth softened by the embrace of water.' } },
  { a: 'Fire', b: 'Earth', result: { name: 'Lava', icon: '🌋', tier: 1, category: 'Nature', description: 'The fiery blood of the earth flows.' } },
  { a: 'Water', b: 'Wind', result: { name: 'Rain', icon: '🌧️', tier: 2, category: 'Nature', description: 'Tears shed by the sky, yearning for the earth.' } },
  { a: 'Fire', b: 'Wind', result: { name: 'Smoke', icon: '💨', tier: 1, category: 'Nature', description: 'A farewell carried on the wind from a dying flame.' } },
  { a: 'Steam', b: 'Wind', result: { name: 'Typhoon', icon: '🌪️', tier: 3, category: 'Nature', description: 'The sky\'s rage swirling into chaos.' } },
  { a: 'Lava', b: 'Water', result: { name: 'Stone', icon: '🪨', tier: 2, category: 'Nature', description: 'What was hot becomes eternal when cooled.' } },
  { a: 'Mud', b: 'Fire', result: { name: 'Brick', icon: '🧱', tier: 2, category: 'Civilization', description: 'Proof that patience endures the trial of fire.' } },
  { a: 'Stone', b: 'Fire', result: { name: 'Glass', icon: '🧪', tier: 2, category: 'Civilization', description: 'The hard becomes transparent when melted.' } }
];

const GENERATED_NAMES: readonly string[] = [
  'Aura', 'Wave', 'Crystal', 'Spirit', 'Vortex', 'Ray', 'Mist', 'Sunset', 'Echo', 'Remnant',
  'Ripple', 'Meteor', 'Leap', 'Blessing', 'Temple', 'Radiance', 'Frost', 'Flash', 'Abyss', 'Firmament',
  'Afterglow', 'Warmth', 'Fence', 'Starlight', 'Dawn', 'Twilight', 'Fragment', 'Ore', 'Spring', 'Purify',
  'Origin', 'Tremor', 'Fortify', 'Evolve', 'Pulse', 'Fusion', 'Reflux', 'Signal', 'Bond', 'Transform',
  'Cycle', 'Illusion', 'Glimmer', 'Serenity', 'Storm', 'Seed', 'Resonance', 'Harmony', 'Kinship', 'Growth',
  'Torrent', 'Pattern', 'City', 'Village', 'Circuit', 'Steel', 'Maelstrom', 'Memory', 'Nostalgia', 'Archive',
  'Link', 'Saga', 'Destiny', 'Stardust', 'Shimmer', 'Blaze', 'Gold', 'Heart', 'Essence', 'Hope'
];

export const CATEGORY_ICONS: Record<ElementCategory, string> = {
  Nature: '🌿',
  Life: '🧬',
  Civilization: '🏛️',
  Food: '🍽️',
  Concept: '💡',
  Modern: '📱',
  Mythology: '🐉',
  Space: '🌌',
  Art: '🎨',
  Other: '✨'
};

const CATEGORIES: readonly ElementCategory[] = ['Nature', 'Life', 'Civilization', 'Food', 'Concept', 'Modern', 'Mythology', 'Space', 'Art', 'Other'];

export function toComboKey(a: string, b: string): string {
  return [a, b].sort((left, right) => left.localeCompare(right, 'en-US')).join('+');
}

export const KNOWN_RECIPES_BY_KEY: Readonly<Record<string, ElementInfo>> = Object.freeze(
  KNOWN_RECIPE_LIST.reduce<Record<string, ElementInfo>>((acc, recipe) => {
    acc[toComboKey(recipe.a, recipe.b)] = { ...recipe.result, origin: 'known' };
    return acc;
  }, {})
);

const KNOWN_RESULT_ICONS: Readonly<Record<string, string>> = Object.freeze(
  KNOWN_RECIPE_LIST.reduce<Record<string, string>>((acc, recipe) => {
    acc[recipe.result.name] = recipe.result.icon;
    return acc;
  }, {})
);

export function createBaseElementMap(): Record<string, ElementInfo> {
  return BASE_ELEMENTS.reduce<Record<string, ElementInfo>>((acc, element) => {
    acc[element.name] = { ...element };
    return acc;
  }, {});
}

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function createGeneratedElement(
  a: ElementInfo,
  b: ElementInfo,
  existingNames: ReadonlySet<string>
): ElementInfo {
  const key = toComboKey(a.name, b.name);
  const seed = hashString(key);
  const maxTier = Math.max(a.tier, b.tier);
  const tier = Math.min(6, maxTier + (seed % 2));
  const category = CATEGORIES[seed % CATEGORIES.length];

  let index = seed % GENERATED_NAMES.length;
  let attempts = 0;
  let resultName = GENERATED_NAMES[index];

  while (existingNames.has(resultName) && attempts < GENERATED_NAMES.length) {
    index = (index + 17) % GENERATED_NAMES.length;
    resultName = GENERATED_NAMES[index];
    attempts += 1;
  }

  if (existingNames.has(resultName)) {
    resultName = 'Genesis';
  }

  return {
    name: resultName,
    icon: CATEGORY_ICONS[category],
    tier,
    category,
    origin: 'generated'
  };
}

export function resolveElementIcon(name: string, category: ElementCategory): string {
  return KNOWN_RESULT_ICONS[name] ?? CATEGORY_ICONS[category] ?? '✨';
}
